/**
 * Centralized product URL resolver.
 *
 * Environment detection priority:
 *   1. VITE_ENV env var (set explicitly in .env files)
 *   2. Vite's import.meta.env.DEV / PROD (set by Vite based on mode)
 *   3. Fallback: window.location.hostname check
 *
 * Port map (ensure vite configs match):
 *   auth.podus.org          → 3000
 *   chat.podus.ai            → 3001
 *   podus.ai (landing)       → 3010
 *   harmlist.com             → 3002
 *   podus.org                → 3003
 *   checker.podus.ai        → 3004
 *   iris.citizensgavel.org   → 3005
 *   app.podus.ai             → 3006
 *   contract.podus.ai        → 3006  (same app)
 *   appointment.podus.ai     → 3006  (same app)
 *   multi-door.podus.org     → 3007  (legacy domain)
 *   multidoor.podus.ai       → 3007  (canonical)
 *   app.podus.org            → 3008
 */

function getImportMetaEnv(): Record<string, unknown> | undefined {
    try {
        return (import.meta as unknown as { env?: Record<string, unknown> }).env ?? undefined;
    } catch {
        return undefined;
    }
}

function detectEnv(): 'local' | 'production' {
    const env = getImportMetaEnv();

    // 1. Explicit env var override (set VITE_ENV=local or VITE_ENV=production in .env)
    if (env) {
        const explicit = env.VITE_ENV;
        if (explicit === 'local' || explicit === 'production') return explicit;
    }

    // 2. Vite's built-in mode detection (DEV = true when running `vite dev`)
    if (env) {
        if (env.DEV) return 'local';
        if (env.PROD) return 'production';
    }

    // 3. Fallback: hostname check
    if (
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ) {
        return 'local';
    }

    return 'production';
}

/** Lazy env — evaluated on first access so Vite's replacements and window.location are both available. */
let _env: 'local' | 'production' | undefined;
function getEnv() {
    if (!_env) _env = detectEnv();
    return _env;
}
function getIsDev() {
    return getEnv() === 'local';
}

const LOCAL_PORT_MAP: Record<string, number> = {
    'auth.podus.org': 3000,
    'podus.ai': 3010,
    'chat.podus.ai': 3001,
    'harmlist.com': 3002,
    'podus.org': 3003,
    'www.podus.org': 3003,
    'checker.podus.ai': 3004,
    'iris.citizensgavel.org': 3005,
    'ipvp.citizensgavel.org': 3005,
    'app.podus.ai': 3006,
    'contract.podus.ai': 3006,
    'appointment.podus.ai': 3006,
    'multi-door.podus.org': 3007,
    'multidoor.podus.ai': 3007,
    'app.podus.org': 3008,
    'help.podus.org': 3009,
};

/**
 * Resolve a production URL to its local equivalent when running in dev.
 *
 * @example
 *   resolveProductUrl("https://chat.podus.ai")
 *   // dev  → "http://localhost:3001"
 *   // prod → "https://chat.podus.ai"
 */
export function resolveProductUrl(productionUrl: string): string {
    if (!getIsDev()) return productionUrl;

    try {
        const parsed = new URL(productionUrl);
        const port = LOCAL_PORT_MAP[parsed.hostname];
        if (port) {
            return `http://localhost:${port}${parsed.pathname === '/' ? '' : parsed.pathname}${parsed.search}${parsed.hash}`;
        }
    } catch {
        // not a valid URL — return as-is
    }

    return productionUrl;
}

/** Shorthand for the auth service URL — resolved lazily at access time. */
export function getAuthUrl(): string {
    return getIsDev() ? 'http://localhost:3000' : 'https://auth.podus.org';
}

export const AUTH_URL = 'https://auth.podus.org';

/** Named product URL map — lazy getters so env is resolved at access time, not module init. */
export const PRODUCT_URLS = {
    get podusai() {
        return resolveProductUrl('https://podus.ai');
    },
    get chatpodusai() {
        return resolveProductUrl('https://chat.podus.ai');
    },
    get podusaid() {
        return resolveProductUrl('https://app.podus.org/dashboard');
    },
    get podusappointment() {
        return resolveProductUrl('https://appointment.podus.ai');
    },
    get poduscontract() {
        return resolveProductUrl('https://contract.podus.ai');
    },
    get poduschecker() {
        return resolveProductUrl('https://checker.podus.org');
    },
    get podusmultidoor() {
        return resolveProductUrl('https://multidoor.podus.ai');
    },
    get podushelp() {
        return resolveProductUrl('https://help.podus.org');
    },
    get podus() {
        return resolveProductUrl('https://podus.org');
    },
};

/** Current resolved environment. */
export { getEnv as PODUS_ENV };
